/**
 * Renders one notification body from a structured payload.
 *
 * Look up the i18n string at ``notifications.kinds.<kind>.body`` (or
 * ``.subject``), then walk the tokens. ``{slot}`` matches an
 * ``entity_refs`` slot first → renders as ``<Link>``; otherwise
 * matches a ``template_vars`` key → plain text. Tokens that match
 * neither show up as the literal placeholder ``{slot}`` so missing
 * data is visible in dev. Production deploys catch unknown tokens at
 * compose time on the server side.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { parseTokens } from './tokens';

export interface EntityRef {
  slot: string;
  kind: string;
  id: string;
  label: string;
  url: string;
}

interface BaseProps {
  kind: string;
  template_vars?: Record<string, string>;
  entity_refs?: EntityRef[];
  /** Override the translation key suffix; defaults to 'body'. */
  field?: 'body' | 'subject';
  /** Fallback string used when no i18n key matches (e.g. legacy rows). */
  fallback?: string | null;
}

export function NotificationBody({
  kind,
  template_vars = {},
  entity_refs = [],
  field = 'body',
  fallback = null,
}: BaseProps): JSX.Element {
  const { t } = useTranslation('notifications');
  const key = `kinds.${kind}.${field}`;
  const text = t(key, { defaultValue: fallback ?? '' }) || fallback || '';
  const slots = new Map(entity_refs.map((r) => [r.slot, r]));
  const parts: ReactNode[] = [];
  for (const token of parseTokens(text)) {
    if (token.kind === 'text') {
      parts.push(token.value);
      continue;
    }
    const slot = slots.get(token.value);
    if (slot) {
      parts.push(
        <Link
          key={`${token.value}-${parts.length}`}
          to={slot.url}
          className="font-medium text-primary hover:underline"
          data-slot={slot.slot}
        >
          {slot.label}
        </Link>,
      );
      continue;
    }
    const value = template_vars[token.value];
    if (typeof value === 'string') {
      parts.push(value);
      continue;
    }
    parts.push(`{${token.value}}`);
  }
  return <>{parts}</>;
}
